/* eslint-disable no-param-reassign */
import React, { JSX, useCallback, useEffect, useMemo } from 'react';

import { ListRenderItemInfo, View } from 'react-native';

import Animated, {
    SharedValue,
    useAnimatedScrollHandler,
} from 'react-native-reanimated';

import { isWeb } from 'utils/responsiveness';

import ChannelCellRenderer from '../Renderers/ChannelCellRenderer';
import useEpgDragHandlers from '../hooks/useEpgDragHandlers';
import { EPGChannel, EPGChannels, EPGConfig } from '../types';
import getStyles from './styles';

type Props = {
    config: EPGConfig;
    listRef?: React.Ref<Animated.FlatList<EPGChannel<any>>>;
    channels: EPGChannels<any>;
    yScrollOffset: SharedValue<number>;
    skipYScrollOffsetUpdate: SharedValue<boolean>;
    renderCell: (channel: EPGChannel<any>) => JSX.Element;
    onTouchStart?: () => void;
    onCellPress?: (channel: EPGChannel<any>) => void;
    onChannelAccessibilityAction?: (channel: EPGChannel<any>) => void;
};

const ChannelList = ({
    config,
    listRef,
    channels,
    yScrollOffset,
    skipYScrollOffsetUpdate,
    renderCell,
    onTouchStart,
    onCellPress,
    onChannelAccessibilityAction,
}: Props) => {
    const dataClass = 'epgChannelList';

    useEpgDragHandlers({
        dataClass,
        offsetY: yScrollOffset,
        isVertical: true,
    });

    const styles = useMemo(() => getStyles(config), []);

    useEffect(() => {
        if (isWeb) {
            const refObject = listRef as React.RefObject<
                Animated.FlatList<EPGChannel<any>>
            >;
            if (refObject?.current) {
                refObject.current.scrollToOffset({
                    offset: yScrollOffset.value,
                    animated: false,
                });
            }
        }
    }, [listRef, yScrollOffset.value]);

    const scrollHandler = useAnimatedScrollHandler((e) => {
        if (!skipYScrollOffsetUpdate.value) {
            const { y } = e.contentOffset;
            yScrollOffset.value = y;
        }
    }, []);

    const getItemLayout = useCallback((_: any, index: number) => {
        const length = config.rowHeight + config.padding;
        return {
            index,
            length,
            offset: index * length,
        };
    }, []);

    const renderItem = useCallback(
        (info: ListRenderItemInfo<unknown>) => (
            <ChannelCellRenderer
                config={config}
                item={info.item as EPGChannel<any>}
                renderContent={renderCell}
                onCellPress={onCellPress}
                onCellAccessibilityAction={onChannelAccessibilityAction}
            />
        ),
        [onCellPress, renderCell, onChannelAccessibilityAction],
    );

    return (
        <View style={styles.container} dataSet={{ class: dataClass }}>
            {!!channels.length && (
                <Animated.FlatList
                    // @ts-ignore - TODO: fixme
                    ref={listRef}
                    data={channels}
                    renderItem={renderItem}
                    getItemLayout={getItemLayout}
                    onTouchStart={onTouchStart}
                    onScroll={scrollHandler}
                    scrollEventThrottle={16}
                    decelerationRate={config.decelerationRate}
                    showsVerticalScrollIndicator={false}
                    bounces={false}
                />
            )}
        </View>
    );
};

export default ChannelList;
